import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { IconFileX, IconSearchPlus, IconUpload, PlusOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { DropdownMenu } from '../../../components/ui/DropdownMenu'

export interface WorkspaceUploadMenuProps {
  /** The project this button lives on — threaded to `/uploads` so its
   * "Uploading to" selector preselects THIS project rather than falling
   * back to "first joined" (UID-433 gap #2). */
  readonly workspaceId: string
  /** Gates ONLY the "Upload files or CAD" item (code-review C2) — its
   * destination is the one item of the three that actually needs
   * `bom:upload`. "Copy from Excel"/"Add by part number" resolve to
   * `cart:manage`/`catalog:read`, both `BUYER_BASELINE` (`authz.ts`) and
   * so held by every customer role; gating the whole menu on `bom:upload`
   * would hide two actions a buyer/purchaser/account_admin IS permitted to
   * use. */
  readonly canUpload: boolean
}

interface UploadMenuOption {
  readonly title: string
  readonly sub: string
  readonly icon: ReactNode
  readonly onClick: () => void
}

/**
 * UID-433 — the Parts-tab "Upload" button's missing dropdown
 * (`Fictiv Platform.dc.html:9955-9971`, `wsView.uploadOptions`): 3 items,
 * each an icon tile + title + subline, matching `AddToProjectMenu`'s shape
 * on the same shared `DropdownMenu` shell. "Copy from Excel" and "Add by
 * part number" both end at the SAME screens the comp's own
 * `applyXl()`/catalog nav do (cart and catalog respectively, not this
 * project's parts) — so both are plain navigation, no new backend/flow.
 */
export function WorkspaceUploadMenu({ workspaceId, canUpload }: WorkspaceUploadMenuProps) {
  const navigate = useNavigate()

  const options: readonly UploadMenuOption[] = [
    ...(canUpload
      ? [
          {
            title: 'Upload files or CAD',
            sub: 'STEP, IGES, drawings, or a BOM',
            icon: <IconUpload size={17} />,
            onClick: () => navigate('/uploads', { state: { workspaceId } }),
          },
        ]
      : []),
    {
      title: 'Copy from Excel',
      sub: 'Paste part numbers and quantities',
      icon: <IconFileX size={17} />,
      // The comp's `applyXl()` (`:6805`) ends with `this.go('cart')` — adds
      // to the CART, not this project's parts. `CartPage` auto-opens
      // `CopyFromExcelModal` on this handoff (`openCopyFromExcel` state).
      onClick: () => navigate('/cart', { state: { openCopyFromExcel: true } }),
    },
    {
      title: 'Add by part number',
      sub: 'Search the MISUMI / Fictiv catalog',
      icon: <IconSearchPlus size={17} />,
      // The comp's catalog nav (`:6945` etc.) is a plain browse-stage
      // navigation, not a project-scoped add.
      onClick: () => navigate('/catalog'),
    },
  ]

  return (
    <DropdownMenu
      className="workspace-upload-menu"
      menuLabel="Upload"
      renderTrigger={({ open, toggle, triggerRef }) => (
        <Button
          ref={triggerRef}
          variant="secondary"
          className="workspace-toolbar-upload-btn"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
        >
          {/* Comp `:2618` — the trigger's own glyph is a plain "+"
           * (`M12 5v14M5 12h14`), matching every other toolbar primary
           * button (RFQ's "New RFQ", BOMs' "New BOM"). `IconUpload` is the
           * correct glyph for the FIRST menu item below (comp's own
           * `iconPath` for that row), not for the trigger. */}
          <PlusOutlined aria-hidden="true" />
          Upload
        </Button>
      )}
      renderMenu={(close) =>
        options.map((option) => (
          <button
            key={option.title}
            type="button"
            role="menuitem"
            className="dropdown-menu-item workspace-upload-menu-item"
            onClick={() => {
              close()
              option.onClick()
            }}
          >
            <span className="workspace-upload-menu-tile" aria-hidden="true">
              {option.icon}
            </span>
            <span className="workspace-upload-menu-text">
              <span className="workspace-upload-menu-title">{option.title}</span>
              <span className="workspace-upload-menu-sub">{option.sub}</span>
            </span>
          </button>
        ))
      }
    />
  )
}
